import { additionalChainsNotInDB } from './../utils/chains/additionalChainsNotInDB';
import { getSupportedChains } from '@/services/utility.service';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export interface IChain {
	chainId: number;
	chainSymbol?: string;
	chainName?: string;
	chainLogo?: string;
	openSeaUrl?: string;
	blockExplorerDomain: string;
	isCampaignRewardErc20: boolean;
	isCampaignRewardErc1155: boolean;
	isNoCodeAvailable: boolean;
	isBlockExplorerAvailable: boolean;
	isRpcUrlsAvailable: boolean;
	isTestnet: boolean;
	isMoralisAvailable: boolean;
	isCustom?: boolean;
}

export const useGetSupportedChains = ({
	showAdditionalChains = false,
	fetchEnterpriseChains = false,
	showNonEVM = false,
}: {
	showAdditionalChains?: boolean;
	fetchEnterpriseChains?: boolean;
	showNonEVM?: boolean;
}) => {
	const [mergedChains, setMergedChains] = useState([]);

	const { data, isLoading } = useQuery<IChain[]>({
		queryKey: ['chains', fetchEnterpriseChains],
		queryFn: () => getSupportedChains(fetchEnterpriseChains),
		staleTime: Infinity,
	});

	function mergeChains(originalChains: any[], newChains: any[]): any[] {
		if (!originalChains) return newChains;
		const existingChainNames = new Set(
			originalChains.map((chain) => chain.chainName),
		);
		const filteredNewChains = newChains.filter(
			(chain) => !existingChainNames?.has(chain.chainName),
		);
		return [...originalChains, ...filteredNewChains];
	}
	const getChainOptions = (chains: IChain[]) => {
		return (
			chains?.map((chain) => ({
				label: chain.chainName,
				value: chain.chainId,
				img: chain.chainLogo,
			})) ?? []
		);
	};

	const getChainName = (chainId: number) => {
		const chain = mergedChains?.find(
			(chain) => Number(chain.chainId) === Number(chainId),
		);
		return chain?.chainName;
	};

	const getNativeChainLogo = (chainId: number) => {
		const chain = mergedChains?.find(
			(chain) => Number(chain.chainId) === Number(chainId),
		);
		return chain?.chainLogo;
	};

	useEffect(() => {
		let updatedChains = data;
		if (data && showAdditionalChains) {
			updatedChains = mergeChains(data, additionalChainsNotInDB);
		}
		if (updatedChains && showNonEVM) {
			updatedChains = mergeChains(updatedChains, nonEVMChains);
		}
		setMergedChains(updatedChains);
	}, [data, showAdditionalChains, showNonEVM]);

	return {
		chains: data,
		isLoading,
		getChainOptions,
		tokenChains: data?.filter((chain) => chain.isCampaignRewardErc20),
		nftChains: data?.filter((chain) => chain.isCampaignRewardErc1155),
		noCodeChains: data?.filter((chain) => chain.isNoCodeAvailable),
		getChainName,
		getNativeChainLogo,
	};
};

const nonEVMChains = [
	{
		chainName: 'Solana',
		chainLogo:
			'https://upload.wikimedia.org/wikipedia/en/b/b9/Solana_chainLogo.png',
		chainId: -10001,
	},
	{
		chainName: 'Bitcoin',
		chainLogo: 'https://icons.llamao.fi/icons/chains/rsz_bitcoin?w=48&h=48',
		chainId: -10002,
	},
	{
		chainName: 'Sui',
		chainLogo: 'https://icons.llamao.fi/icons/chains/rsz_sui?w=48&h=48',
		chainId: -10003,
	},
	{
		// sui, aptos, sei
		chainName: 'Hyperliquid',
		chainLogo: 'https://icons.llamao.fi/icons/chains/rsz_hyperliquid?w=48&h=48',
		chainId: -10004,
	},
	{
		chainName: 'Aptos',
		chainLogo: 'https://icons.llamao.fi/icons/chains/rsz_aptos?w=48&h=48',
		chainId: -10006,
	},
	{
		chainName: 'Starknet',
		chainLogo: 'https://icons.llamao.fi/icons/chains/rsz_starknet?w=48&h=48',
		chainId: -10008,
	},
	{
		chainName: 'Injective',
		chainLogo: 'https://icons.llamao.fi/icons/chains/rsz_injective?w=48&h=48',
		chainId: -10009,
	},
	{
		chainName: 'Cosmos',
		chainLogo:
			'https://oss.web3ite.io/logo/b1d031475299801e979dba921db0206c.jpg?x-oss-process=image/resize,w_800/quality,q_80/format,webp',
		chainId: -10020,
	},
];
